import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPalette } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import Dropdown from 'components/shared/Dropdown';
import { MainContext } from 'contexts/MainContext';
import { THEMES, ThemeId, applyTheme, readTheme, themeId, themeLabels } from 'utils/theme';
import { saveUserConfig } from 'services/user';
import useRecaptcha from 'hooks/useRecaptcha';

const ThemeSwitcher: FC = () => {
  const { userSiteConfig, setUserSiteConfig } = useContext(MainContext);
  const { handleRecapcha } = useRecaptcha();
  const { i18n } = useTranslation();
  const [theme, setTheme] = useState<ThemeId>(() =>
    userSiteConfig?.base?.theme ? themeId(userSiteConfig.base.theme) : readTheme()
  );
  const labels = themeLabels[i18n.language] || themeLabels.en;

  const options = useMemo(
    () =>
      THEMES.map((item, index) => ({
        id: item.id,
        name: labels?.names?.[index] || item.name,
      })),
    [labels]
  );

  useEffect(() => {
    if (userSiteConfig?.base?.theme) {
      setTheme(themeId(userSiteConfig.base.theme));
    }
  }, [userSiteConfig?.base?.theme]);

  const onSelect = async (id: ThemeId) => {
    const applied = applyTheme(id);
    setTheme(applied);

    const nextConfig = {
      ...userSiteConfig,
      base: {
        ...userSiteConfig.base,
        theme: applied,
      },
    };
    setUserSiteConfig(nextConfig);

    const recaptchaToken = await handleRecapcha();
    const res = await saveUserConfig({
      recaptchaToken,
      input: nextConfig,
    });

    if (res?.success) {
      try {
        const userLocal = JSON.parse(localStorage.getItem('user') || '{}');
        userLocal.config = nextConfig;
        localStorage.setItem('user', JSON.stringify(userLocal));
      } catch {}
    }
  };

  return (
    <div className="header-switch-theme">
      <Dropdown
        opener={
          <FontAwesomeIcon
            icon={faPalette as any}
            className="fa-palette"
            title={labels?.label || 'Theme'}
            aria-label={labels?.label || 'Theme'}
          />
        }
      >
        {options.map((option) => (
          <div
            className={'c-dropdown-row' + (option.id === theme ? ' active' : '')}
            key={option.id}
            onClick={() => onSelect(option.id)}
          >
            <span className="text-sm">{option.name}</span>
          </div>
        ))}
      </Dropdown>
    </div>
  );
};

export default ThemeSwitcher;
